import type { IAdjustmentRule } from "./types";
import { getTransitionDate, timeSpanToMs } from "./dstCalculator";

// Support for zones whose standard UTC offset changed (for example British Columbia and
// Alberta on 2026-11-01). .NET expresses such a change as `baseUtcOffsetDelta` on the
// adjustment rules covering the new period, on top of the zone's `offsetFromUtc`; the
// calculator's `offsetFromUtc + daylightDelta` model cannot represent it on its own. This
// module resolves that shift for an instant. Most of it exists to place the boundary between
// two rules at the real transition instant rather than at midnight UTC of a calendar date,
// which only matters on the day the offset changes. Its only consumer is `getOffset`.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// getOffset runs for every rendered timestamp, so rule bounds are parsed once per string.
const ruleDateMemo = new Map<string, number>();

const ruleDateToMs = (date: string): number => {
    let cached = ruleDateMemo.get(date);
    if (cached === undefined) {
        cached = new Date(date).getTime();
        ruleDateMemo.set(date, cached);
    }
    return cached;
};

// Shift to the zone's standard offset for a rule's period, in minutes. Unlike
// `daylightDelta` it applies for the whole period, in and out of daylight saving.
const getBaseDeltaMinutes = (rule: IAdjustmentRule): number =>
    rule.baseUtcOffsetDelta
        ? timeSpanToMs(rule.baseUtcOffsetDelta) / 1000 / 60
        : 0;

const getDaylightDeltaMinutes = (rule: IAdjustmentRule): number =>
    timeSpanToMs(rule.daylightDelta) / 1000 / 60;

// Bounds of a rule's period, as the UTC instant and as the same wall-clock time expressed in
// the browser's zone (the representation used when converting local input back to UTC; see
// toReverseOffsetDateTime).
interface IRuleBounds {
    rule: IAdjustmentRule;
    start: number;
    end: number;
    startForReverse: number;
    endForReverse: number;
}

const toBrowserWallClockMs = (ms: number): number =>
    ms + new Date(ms).getTimezoneOffset() * 60 * 1000;

// Where a rule's period ends. dateStart/dateEnd are local calendar dates, so they cannot locate
// the boundary on their own. A rule that describes a single period carries its end as a
// fixed-date transition on its last day, expressed in standard time plus the rule's daylight
// delta; that is the precise boundary. A rule whose end transition falls elsewhere describes
// yearly daylight-saving transitions inside the period, so its period runs to the end of its
// last day.
const getRuleEnd = (
    rule: IAdjustmentRule,
    baseOffset: number
): { end: number; endForReverse: number } => {
    let endDateMs = ruleDateToMs(rule.dateEnd);
    let endDate = new Date(endDateMs);
    let end = rule.daylightTransitionEnd;
    if (
        end.isFixedDateRule &&
        end.month === endDate.getUTCMonth() + 1 &&
        end.day === endDate.getUTCDate()
    ) {
        let transition = getTransitionDate(
            end,
            endDate.getUTCFullYear(),
            baseOffset + getDaylightDeltaMinutes(rule)
        );
        return {
            end: transition.userTimeZone.getTime() + 1,
            endForReverse: transition.browserTimeZone.getTime() + 1,
        };
    }
    let nextDayMs = endDateMs + MS_PER_DAY;
    return { end: nextDayMs, endForReverse: toBrowserWallClockMs(nextDayMs) };
};

// Period bounds per rule set, computed once. Rules are contiguous, so each rule starts where
// the previous one ends; the first rule starts at its own date. Reset when the browser zone
// changes because the reverse bounds depend on it.
let ruleBoundsMemo = new WeakMap<IAdjustmentRule[], IRuleBounds[]>();

// Whether any rule in a set shifts the standard offset, computed once per set. Most zones
// never do, and a server with the field disabled sends none, so getOffset skips this module.
const hasStandardOffsetChangeMemo = new WeakMap<IAdjustmentRule[], boolean>();

export const clearRuleBoundsMemo = () => {
    ruleBoundsMemo = new WeakMap<IAdjustmentRule[], IRuleBounds[]>();
};

export const hasStandardOffsetChange = (rules: IAdjustmentRule[]): boolean => {
    let cached = hasStandardOffsetChangeMemo.get(rules);
    if (cached === undefined) {
        cached = rules.some((rule) => getBaseDeltaMinutes(rule) !== 0);
        hasStandardOffsetChangeMemo.set(rules, cached);
    }
    return cached;
};

const getRuleBounds = (
    rules: IAdjustmentRule[],
    baseOffset: number
): IRuleBounds[] => {
    let cached = ruleBoundsMemo.get(rules);
    if (cached) {
        return cached;
    }

    let sorted = rules
        .slice()
        .sort((r1, r2) => ruleDateToMs(r1.dateStart) - ruleDateToMs(r2.dateStart));
    let bounds: IRuleBounds[] = [];
    let previous: IRuleBounds | undefined;
    for (let i = 0; i < sorted.length; ++i) {
        let rule = sorted[i];
        let startMs = ruleDateToMs(rule.dateStart);
        let ruleBounds: IRuleBounds = {
            rule,
            start: previous ? previous.end : startMs,
            startForReverse: previous
                ? previous.endForReverse
                : toBrowserWallClockMs(startMs),
            ...getRuleEnd(rule, baseOffset),
        };
        bounds.push(ruleBounds);
        previous = ruleBounds;
    }
    ruleBoundsMemo.set(rules, bounds);
    return bounds;
};

// Shift to the zone's standard offset at this time, in minutes. A zone's standard offset can
// change part way through a year, so the shift is taken from the rule whose period covers the
// time rather than from whichever rule supplies the year's daylight transitions. Takes the
// zone's full rule set so that boundaries at the turn of the year resolve too. `toLocalDate`
// selects which representation `dateTime` is in, as in getDSTOffset.
export const getBaseDeltaForDate = (
    rules: IAdjustmentRule[],
    dateTime: Date,
    baseOffset: number,
    toLocalDate: boolean = true
): number => {
    if (!hasStandardOffsetChange(rules)) {
        return 0;
    }
    let time = dateTime.getTime();
    let bounds = getRuleBounds(rules, baseOffset);
    for (let i = 0; i < bounds.length; ++i) {
        let b = bounds[i];
        let start = toLocalDate ? b.start : b.startForReverse;
        let end = toLocalDate ? b.end : b.endForReverse;
        if (time >= start && time < end) {
            return getBaseDeltaMinutes(b.rule);
        }
    }
    return 0;
};
